import {LocalDate, nativeJs} from 'js-joda'
import DatePicker from 'material-ui/DatePicker'
import MenuItem from 'material-ui/MenuItem'
import SelectField from 'material-ui/SelectField'
import TextField from 'material-ui/TextField'
import * as R from 'ramda'
import * as React from 'react'
import * as Autosuggest from 'react-autosuggest'
import * as Nullable from '../../Data/Nullable'
import * as BpsUser from '../../Entities/BpsUser'
import {Company} from '../../Entities/Company'
import {OrderPaid, orderPaidAll, orderPaidLabel} from '../../Entities/OrderPaid'
import * as OrderStatus from '../../Entities/OrderStatus'
import {ShippingMethod} from '../../Entities/ShippingMethod'
import {ActiveFiltersView} from '../../View/Filtering/ActiveFiltersView'
import {FilterContainer} from '../../View/Filtering/FilterContainer'
import {FilterForm} from '../../View/Filtering/FilterForm'
import {
    renderSuggestion,
    renderSuggestionsContainer,
    shouldRenderSuggestions
} from '../../View/MaterialAutosuggest'
import * as OrderFilter from './Model/OrderFilter'
import {OrderFilteringState} from './Model/OrderFilteringState'
import {OrderFilterInput} from './Model/OrderFilterInput'
import * as OrderFilterType from './Model/OrderFilterType'
import {OrderFilteringAction} from './OrderFilteringActions'
import TextFieldProps = __MaterialUI.TextFieldProps

export function OrderFilterContainer({state, dispatch}: {
    readonly state: OrderFilteringState
    readonly dispatch: (action: OrderFilteringAction) => void
}) {
    return (
        <FilterContainer
            filterForm={
                <OrderFilterForm
                    state={state}
                    dispatch={dispatch}
                />
            }
            activeFiltersView={
                <OrderActiveFiltersView
                    state={state}
                    dispatch={dispatch}
                />
            }
            filterPopoverOpen={state.popoverOpen}
            onFilterPopoverToggle={
                open =>
                    dispatch({type: 'PopoverToggle', open})
            }
        />
    )
}
export function OrderFilterForm({state, dispatch}: {
    state: OrderFilteringState,
    dispatch: (action: OrderFilteringAction) => void,
}) {
    return (
        <FilterForm
            filterTypeLabel={OrderFilterType.label}
            state={state}
            onFilterSelectChanged={
                (value: OrderFilterType.OrderFilterType) =>
                    dispatch({type: 'FilterSelectChanged', value})
            }
            onAddFilter={() => dispatch({type: 'AddFilter'})}
            onClearFilters={() => dispatch({type: 'ResetFilters'})}
            filterInputView={
                (filterInput: OrderFilterInput) => (
                    <FilterInputView
                        addFilterInput={filterInput}
                        dispatch={dispatch}
                    />
                )
            }
        />
    )
}

export function OrderActiveFiltersView({state, dispatch}: {
    state: OrderFilteringState,
    dispatch: (action: OrderFilteringAction) => void,
}) {
    return (
        <ActiveFiltersView
            filterLabel={OrderFilter.label}
            filterToType={OrderFilter.toFilterType}
            activeFilters={state.activeFilters}
            onDelete={
                (filterType: OrderFilterType.OrderFilterType) =>
                    dispatch({type: 'RemoveFilter', filterType})
            }
        />
    )
}

function FilterInputView({addFilterInput, dispatch}: {
    addFilterInput: OrderFilterInput,
    dispatch: (action: OrderFilteringAction) => void
}): JSX.Element {
    switch (addFilterInput.type) {
        case 'StatusFilterInput':
            return (
                <SelectField
                    className="filter__select"
                    floatingLabelText="Status"
                    value={addFilterInput.value}
                    onChange={(event, key, value: OrderStatus.M) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {type: 'StatusChanged', value}
                        })}
                >
                    {R.map(
                        status =>
                            <MenuItem
                                value={status}
                                primaryText={OrderStatus.label(status)}
                            />,
                        OrderStatus.all(),
                    )}
                </SelectField>
            )
        case 'CustomerFilterInput':
            switch (addFilterInput.customers.type) {
                case 'Loading':
                    return (
                        <SelectField
                            className="filter__select"
                            floatingLabelText="Lade Kundenliste.."
                        >
                            <MenuItem/>
                        </SelectField>

                    )
                case 'Failure':
                    return (
                        <SelectField
                            className="filter__select"
                            floatingLabelText="Fehler beim Laden"
                        >
                            <MenuItem primaryText="Fehler beim Laden"/>
                        </SelectField>

                    )
                case 'Success':
                    return (
                        <SelectField
                            className="filter__select"
                            floatingLabelText="Kunde"
                            value={addFilterInput.value}
                            onChange={(event, key, value: number) =>
                                dispatch({
                                    type: 'InputChanged',
                                    subAction: {type: 'CustomerChanged', value},
                                })}
                        >
                            {addFilterInput.customers.value.map(customer =>
                                <MenuItem
                                    value={customer.id}
                                    primaryText={customer.longName}
                                    key={customer.id}
                                />
                            )}
                        </SelectField>
                    )
                default:
                    return (<p>Unerwartete Antwort</p>)
            }
        case 'BpsUserFilterInput':
            return (
                <Autosuggest
                    suggestions={addFilterInput.bpsUserSuggestions}
                    onSuggestionsFetchRequested={
                        (value: string) => dispatch({
                            type: 'FetchBpsUserSuggestions',
                            value,
                        })}
                    onSuggestionsClearRequested={
                        () => dispatch({
                            type: 'ClearBpsUserSuggestions',
                        })}
                    getSuggestionValue={
                        (nullableBpsUser: BpsUser.BpsUser | null) =>
                            Nullable.map(BpsUser.label, nullableBpsUser)
                    }
                    inputProps={{
                        value: addFilterInput.bpsUserSearch,
                        onChange: (
                            // tslint:disable-next-line
                            event: any,
                            {newValue}: { newValue: string }
                        ) => dispatch({
                            type: 'BpsUserSearchChanged',
                            value: newValue,
                        }),
                    }}
                    onSuggestionSelected={
                        (
                            // tslint:disable-next-line
                            event: any,
                            {suggestion}: { suggestion: BpsUser.BpsUser }
                        ) => dispatch({
                            type: 'InputChanged',
                            subAction: {
                                type: 'BpsUserChanged',
                                value: suggestion,
                            },
                        })}
                    renderInputComponent={
                        (inputProps: TextFieldProps) =>
                            (
                                <TextField
                                    {...inputProps}
                                    floatingLabelText="BpsUser"
                                    fullWidth={true}
                                />
                            )
                    }
                    renderSuggestionsContainer={renderSuggestionsContainer}
                    renderSuggestion={
                        renderSuggestion(
                            (suggestion: BpsUser.BpsUser) =>
                                `${suggestion.firstName} ${suggestion.lastName}`)
                    }
                    shouldRenderSuggestions={shouldRenderSuggestions}
                />
            )
        case 'CompanyFilterInput':
            return (
                <Autosuggest
                    suggestions={addFilterInput.companySuggestions}
                    onSuggestionsFetchRequested={
                        (value: string) => dispatch({
                            type: 'FetchCompanySuggestions',
                            value,
                        })
                    }
                    onSuggestionsClearRequested={
                        () => dispatch({
                            type: 'ClearCompanySuggestions',
                        })
                    }
                    getSuggestionValue={
                        (nullableCompany: Company | null) =>
                            Nullable.map(
                                company => `${company.name1} ${company.name2}`,
                                nullableCompany)
                    }
                    inputProps={{
                        value: addFilterInput.companySearch,
                        onChange: (
                            // tslint:disable-next-line
                            event: any,
                            {newValue}: { newValue: string }
                        ) => dispatch({
                            type: 'CompanySearchChanged',
                            value: newValue,
                        }),
                    }}
                    onSuggestionSelected={
                        (
                            // tslint:disable-next-line
                            event: any,
                            {suggestion}: { suggestion: Company }
                        ) => dispatch({
                            type: 'InputChanged',
                            subAction: {
                                type: 'CompanyChanged',
                                value: suggestion,
                            }
                        })}
                    renderInputComponent={
                        (inputProps: TextFieldProps) =>
                            (
                                <TextField
                                    {...inputProps}
                                    floatingLabelText="Gesellschaft"
                                    fullWidth={true}
                                />
                            )
                    }
                    renderSuggestionsContainer={renderSuggestionsContainer}
                    renderSuggestion={
                        renderSuggestion(
                            (suggestion: Company) =>
                                `${suggestion.name1} ${suggestion.name2}`
                        )
                    }
                    shouldRenderSuggestions={shouldRenderSuggestions}
                />
            )
        case 'OrderNumberFilterInput':
            return (
                <TextField
                    floatingLabelText="Auftragsnummer"
                    type="text"
                    fullWidth={true}
                    value={addFilterInput.value === null ? '' : addFilterInput.value.toString()}
                    onChange={(event, value: string) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {
                                type: 'OrderNumberChanged',
                                value: value,
                            },
                        })}
                />
            )
        case 'ShippingMethodFilterInput':
            return (
                <SelectField
                    className="filter__select"
                    floatingLabelText="Versand"
                    value={addFilterInput.value}
                    onChange={(event, key, value: ShippingMethod) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {
                                type: 'ShippingMethodChanged',
                                value,
                            },
                        })}
                >
                    <MenuItem
                        value="express"
                        primaryText="Express"
                    />
                    <MenuItem
                        value="standard"
                        primaryText="Standard"
                    />
                </SelectField>)
        case 'DateFromFilterInput':
            return (
                <DatePicker
                    hintText="Datum von"
                    floatingLabelText="Bestellungen anzeigen von"
                    value={addFilterInput.value !== null ? new Date(addFilterInput.value.toString()) : undefined}
                    dialogContainerStyle={{zIndex: 3000}}
                    onChange={(event, value: Date) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {
                                type: 'DateFromChanged',
                                value: LocalDate.from(nativeJs(value)),
                            },
                        })}
                />)
        case 'DateToFilterInput':
            return (
                <DatePicker
                    hintText="Datum bis"
                    floatingLabelText="Bestellungen anzeigen bis"
                    value={addFilterInput.value !== null ? new Date(addFilterInput.value.toString()) : undefined}
                    dialogContainerStyle={{zIndex: 3000}}
                    onChange={(event, value: Date) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {
                                type: 'DateToChanged',
                                value: LocalDate.from(nativeJs(value)),
                            }
                        })}
                />)
        case 'PriceFromFilterInput':
            return (
                <TextField
                    floatingLabelText="Preis von"
                    type="number"
                    fullWidth={true}
                    value={addFilterInput.value}
                    onChange={(event, value: string) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {type: 'PriceFromChanged', value},
                        })}
                />
            )
        case 'PriceToFilterInput':
            return (
                <TextField
                    floatingLabelText="Preis bis"
                    type="number"
                    fullWidth={true}
                    value={addFilterInput.value}
                    onChange={(event, value: string) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {type: 'PriceToChanged', value},
                        })}
                />
            )
        case 'OrderPaidFilterInput':
            return (
                <SelectField
                    className="filter__select"
                    floatingLabelText="Bezahlstatus"
                    value={addFilterInput.value}
                    onChange={(event, key, value: OrderPaid) =>
                        dispatch({
                            type: 'InputChanged',
                            subAction: {type: 'OrderPaidChanged', value},
                        })}
                >
                    {R.map(
                        orderPaid =>
                            <MenuItem
                                value={orderPaid}
                                primaryText={orderPaidLabel(orderPaid)}
                                key={orderPaid}
                            />,
                        orderPaidAll(),
                    )}
                </SelectField>)
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Components/OrderFiltering/OrderFilteringView.tsx